import React from "react"
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {endOfDay, formatISO, startOfYesterday} from "date-fns";
import {useCurrentUser} from "../../auth/CurrentUserProvider";

import Page from "../Page";
import MDBox from "../../materialDashboard/components/MDBox";
import MDButton from "../../materialDashboard/components/MDButton";
import GeoJSONMap from "../../components/maps/GeoJSONMap";
import GeoJSONPointsCollection from "../../components/maps/GeoJSONMap/GeoJSONPointsCollection";

import Card from "@mui/material/Card";
import {LayersControl} from "react-leaflet";

import TableDataLoader from "./Models1Table/TableDataLoader";
import useDataTableFilters from "../../components/DataTable/useDataTableFilters";
import {useAuth} from "../../auth/AuthProvider";
import Box from "@mui/material/Box";
import StationFeatureChip from "../../components/maps/stationFeature/StationFeatureChip";
import useModels1Map from "./useModels1Map";
import renderStationTooltip from "../../components/maps/stationFeature/renderStationFeatureTooltip";

import {ExpandButtonIcon, useExpandButtonIcon} from "../../components/maps/ExpandButtonIcon";

const Models1 = () => {
    const {t} = useTranslation(['models1']);
    const navigate = useNavigate();
    const {currentUser} = useCurrentUser();
    const {workgroupAuthorization} = useAuth();

    // visible columns
    const visibleColumns = currentUser.data.userSettings?.model1ColumnsVisibility

    // data states
    const todayStartISO = formatISO(startOfYesterday());
    const todayEndISO = formatISO(endOfDay(new Date));
    const selectedWorkgroups = currentUser.data.role === "ADMIN" ? [] : currentUser.data.workgroups;
    const filterProps = {
        initialValues: {
            datetimeGte: todayStartISO,
            datetimeLte: todayEndISO,
            selectedWorkgroups,
            stationAclUserId: currentUser.data.id,
            stationAclModel1Write: currentUser.data?.role === "WG_USER",  // if user is a workgroup user, he can only see his own models by default
        }
    };
    const initialFiltersValue = {
        datetime__gte: todayStartISO,
        datetime__lte: todayEndISO,
        station__workgroup_id__in: selectedWorkgroups.map(w => w.id),
        station__acl__model1_write: currentUser.data?.role === "WG_USER",  // if user is a workgroup user, he can only see his own models by default
    }

    const filterState = useDataTableFilters({initialFiltersValue});

    // Station maps states
    const map = useModels1Map({models1Filters: filterState.filters});
    const expandButtonState = useExpandButtonIcon();

    return <Page path={"/models1"}>
        <MDBox px={2}>
            <Box justifyContent="space-between" alignItems="center" display="flex" flexWrap="wrap" gap={2} mb={2}>
                {
                    workgroupAuthorization.models1.create && <MDButton variant="gradient" color="info" onClick={() => navigate("/models1/new")}>
                        {t('newModel1')}
                    </MDButton>

                }
                <ExpandButtonIcon {...expandButtonState} />
            </Box>
            <MDBox ref={map.mapBoxRef}
                   borderRadius="xl"
                   bgColor="grey-300"
                   sx={{height: expandButtonState.mapHeight, scrollMarginTop: 100, overflow: "hidden", mt: 2, boxShadow: 1, position: "relative", zIndex: 2}}>
                <GeoJSONMap mapRef={map.mapRef} data={[]}>
                    <LayersControl position="topright">
                        <LayersControl.Overlay name="stations" checked>
                            <GeoJSONPointsCollection featureCollection={map.geoJSON}
                                                     getSelectedFeatures={map.getSelectedFeatures}
                                                     onFeatureClick={map.handleFeatureClick}
                                                     renderFeatureTooltip={renderStationTooltip}
                                                     cluster
                            />
                        </LayersControl.Overlay>
                    </LayersControl>
                </GeoJSONMap>
            </MDBox>
            <Card sx={{pt: 3, mt: -3, zIndex: 1}}>
                {
                    map.selectedFeatures.length > 0 && <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, pt: 3, px: 3}}>
                        {
                            map.selectedFeatures.map(f =>
                                <StationFeatureChip key={f.properties.id}
                                                    feature={f}
                                                    onDelete={() => map.toggleFeature(f)}/>
                            )
                        }
                    </Box>
                }
                <TableDataLoader filterState={filterState}
                                 filterProps={filterProps}
                                 map={map}
                                 dataTableId="models1"
                                 visibleColumns={visibleColumns}/>
            </Card>
        </MDBox>
    </Page>
}



export default Models1;